import React from "react";

import { RewardResponse } from "src/types/coaching/gamification";

import _ from "lodash";
import cx from "classnames";
import styles from "./rewardCard.module.scss";
import { BannerView } from "src/components/banner";
import Tooltip from "src/components/tooltip";
import LinesEllipsis from "react-lines-ellipsis";

const defaultImage = "/images/collections/background.jpeg";

export interface RewardCardProps {
  reward: RewardResponse;
  onEdit?: (reward: RewardResponse) => void;
  onDelete?: () => void;
}

const RewardCard: React.FC<RewardCardProps> = ({
  reward,
  onEdit,
  onDelete,
}) => {
  const isLimitedQuantity = _.get(reward, "quantity", 0) >= 0;
  const isOutOfStock = isLimitedQuantity && _.get(reward, "quantity", 0) === 0;

  const [showNameTooltip, setShowNameTooltip] = React.useState(false);
  const [showDescriptionTooltip, setShowDescriptionTooltip] = React.useState(false);

  const banner = React.useMemo(() => {
    return _.get(reward, "banner");
  }, [reward]);

  return (
    <div
      className={cx(styles.card, {
        [styles.inactive]: _.get(reward, "disabled", false)
      })}
    >
      <div className={styles.icon}>
        {!isLimitedQuantity && (
        <span className={styles.quantityBadge}>unlimited</span>
        )}
        {isLimitedQuantity && (
          <span className={styles.quantityBadge}>
            {isOutOfStock ? "out of stock" : `${_.get(reward, "quantity", 0)} remaining`}
          </span>
        )}
        <BannerView
          banner={banner as any}
          defaultBanner={defaultImage}
          overlay={isOutOfStock}
        />
      </div>
      <div className={styles.cardContent}>
        <div className={styles.details}>
        <Tooltip
          content={reward.name}
          placement="top"
          disabled={!showNameTooltip}
        >
            <div className={styles.nameWrapper}>
              <h3 className={cx(styles.name, {
                [styles.outOfStock]: isOutOfStock,
              })}>
              <LinesEllipsis
                text={_.get(reward, "name", "")}
                maxLine="1"
                basedOn="letters"
                ellipsis="..."
                onReflow={({ clamped }) =>
                  setShowNameTooltip(clamped)
                }
              />
            </h3>
          </div>
        </Tooltip>
        <Tooltip
          content={reward.description}
          placement="top"
          disabled={!showDescriptionTooltip}
        >
          <div className={styles.descriptionWrapper}>
              <p className={cx(styles.description, {
                [styles.outOfStock]: isOutOfStock,
              })}>
              <LinesEllipsis
                text={_.get(reward, "description", "")}
                maxLine="2"
                basedOn="letters"
                ellipsis="..."
                onReflow={({ clamped }) =>
                  setShowDescriptionTooltip(clamped)
                }
              />
            </p>
          </div>
        </Tooltip>
          <div className={styles.footer}>
            <div className={cx(styles.footerLeft, {
              [styles.outOfStock]: isOutOfStock,
            })}>
              <i className={cx("paperflite-coins", styles.coin)} aria-hidden />
              <p className={styles.points}>
                {_.get(reward, "pointsCost", 0).toLocaleString()} points
              </p>
            </div>
            <div className={styles.cardActions}>
              <button
                type="button"
                className={styles.actionButton}
                aria-label="Edit reward"
                onClick={() => onEdit && onEdit(reward)}
              >
                <i className="paperflite-edit" aria-hidden />
              </button>
              <button
                type="button"
                className={styles.actionButton}
                aria-label="delete reward"
                onClick={() => onDelete && onDelete()}
              >
                <i className="paperflite-delete" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PlaceholderRewardCard: React.FC = () => (
  <div className={cx(styles.card, styles.placeholder)} aria-hidden>
    <div className={cx(styles.icon, styles.block)} />
    <div className={styles.cardContent}>
      <div className={styles.details}>
        <div className={cx(styles.name, styles.block)} />
        <p className={cx(styles.description, styles.block)} />
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <p className={cx(styles.points, styles.block)} />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default RewardCard;
